import type { Ref } from 'vue'
import type { Image } from '~/types/image'
import type { Product } from '~/types/product'

export const DEFAULT_IMAGES: Record<'logo' | 'missing', Image> = {
  logo: {
    imageUrl: '/images/logo/logo-vodi4ka-social.png',
    fileDescr: 'Site logo'
  },
  missing: {
    imageUrl: '/images/default/image-not-available.avif',
    fileDescr: 'Image not available'
  }
}

const getDefaultImage = (type: 'logo' | 'missing') => DEFAULT_IMAGES[type]

const prepareLink = (url: string) => {
  const config = useRuntimeConfig()
  const baseURL = config?.public?.apiBaseUrl ? String(config.public.apiBaseUrl).replace(/\/$/, '') : ''
  return url.replace('/api/w4eshop', baseURL);
}

/** Prefer API fileDescr; fall back to product name when CMS alt is null/blank. */
export function resolveImageAlt(
  fileDescr: string | null | undefined,
  fallbackName: string
): string {
  const trimmed = fileDescr?.trim()
  return trimmed || fallbackName
}

/**
 * Resolve an Image from different input shapes:
 * - Image
 * - Product (uses first element of product.imageUrls)
 * - Image[] (uses first)
 * - Ref<Image | Product | Image[] | null>
 */
export function resolveImage(
  product: Readonly<Ref<Product | null>>,
  defaultType: 'logo' | 'missing' = 'missing'
): Image {

  if (!product.value || !product.value.imageUrls || product.value.imageUrls.length === 0) {
    return getDefaultImage(defaultType)
  }


  return {
    imageUrl: prepareLink(String(product.value.imageUrls[0]!.imageUrl)),
    fileDescr: resolveImageAlt(product.value.imageUrls[0]!.fileDescr, product.value.name),
  }
}

/**
 * Absolute URL for an image referenced in an email. Email clients cannot
 * resolve site-relative paths, so anything still relative after the API base
 * substitution gets prefixed with the canonical site URL.
 */
export function toAbsoluteImageUrl(path: string | null): string | null {
  if (!path) return null

  const resolved = path.startsWith('http') ? path : prepareLink(path)
  if (resolved.startsWith('http')) return resolved

  const base = String(useRuntimeConfig().public.siteUrl).replace(/\/$/, '')
  return `${base}${resolved.startsWith('/') ? resolved : `/${resolved}`}`
}

const PRODUCT_IMAGE_PATH = /\/Public\/ProductImages\/(\d+)(?:\/og\.jpg)?\/?(?:\?.*)?$/

/**
 * Messenger / Open Graph JPEG URL for a product gallery image.
 * Idempotent: URLs that already end with /og.jpg are returned unchanged.
 * Non-product paths (e.g. site logo) are returned unchanged.
 */
export function toOgImageUrl(url: string): string {
  if (!url) return url
  if (/\/Public\/ProductImages\/\d+\/og\.jpg(?:\?.*)?$/i.test(url)) {
    return url
  }
  const match = url.match(PRODUCT_IMAGE_PATH)
  if (!match) return url
  return url.replace(PRODUCT_IMAGE_PATH, `/Public/ProductImages/${match[1]}/og.jpg`)
}

export { getDefaultImage, prepareLink }
